/** Native Shopify quote for this selection; never reads or changes the buyer's cart. */
class FivepointShippingEstimate extends HTMLElement {
  #root; #strings; #listeners; #request; #timer; #version = 0; #key = ''; #destination;
  #get(name) { return this.querySelector(`[data-shipping-${name}]`); }
  #text(key) { return this.#strings[key]; }
  connectedCallback() {
    this.#root = this.closest('fivepoint-pdp');
    this.#strings = JSON.parse(this.#get('strings').textContent);
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    const country = this.#get('country');
    const english = new Intl.DisplayNames(['en'], { type: 'region' });
    for (const option of country.options) option.dataset.english = english.of(option.value);
    this.#destination = { country_code: this.dataset.country, province: this.dataset.province || '', zip: this.dataset.zip || '' };
    try {
      const saved = JSON.parse(sessionStorage.getItem('fivepoint.shipping-destination') || 'null');
      if (saved && [...country.options].some(o => o.value === saved.country_code) && typeof saved.province === 'string' && typeof saved.zip === 'string') this.#destination = saved;
    } catch { /* Storage is an optional preference; quoting does not depend on it. */ }
    this.#get('change').addEventListener('click', this.#open, options);
    this.#get('close').addEventListener('click', () => this.#get('dialog').close(), options);
    this.#get('dialog').addEventListener('close', () => {
      this.#get('change').setAttribute('aria-expanded', 'false');
      this.#get('change').focus({ preventScroll: true });
    }, options);
    this.#get('dialog').addEventListener('click', event => {
      if (event.target !== this.#get('dialog')) return;
      const r = event.target.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) event.target.close();
    }, options);
    country.addEventListener('change', () => this.#regions(), options);
    this.#get('form').addEventListener('submit', this.#apply, options);
    this.#get('retry').addEventListener('click', () => { this.#key = ''; this.#sync(); }, options);
    this.#root.addEventListener('atelier:selection', this.#sync, options);
    window.addEventListener('resize', this.#position, options);
    window.addEventListener('scroll', this.#position, { ...options, passive: true });
    this.#destinationLabel();
    this.#sync();
  }
  disconnectedCallback() {
    this.#listeners?.abort(); this.#request?.abort(); clearTimeout(this.#timer); this.#version++;
  }
  #regions() {
    const country = this.#get('country').selectedOptions[0];
    const source = [...this.#get('regions').options].find(o => o.value === country?.dataset.english || o.textContent === country?.textContent);
    const problem = this.#get('form-error');
    problem.hidden = Boolean(source);
    if (!source) problem.textContent = this.#text('no_rates');
    const provinces = source ? JSON.parse(source.dataset.provinces) : [];
    const select = this.#get('province');
    select.replaceChildren(new Option(this.#text('select_region') || '', ''));
    for (const [value, label] of provinces) select.add(new Option(label, value));
    select.required = provinces.length > 0;
    select.disabled = provinces.length === 0;
    this.#get('province-wrap').hidden = provinces.length === 0;
    this.#get('zip').required = country?.value === 'US';
    this.#get('form').querySelector('[type=submit]').disabled = !source;
  }
  #open = () => {
    this.#get('country').value = this.#destination.country_code;
    this.#regions();
    this.#get('province').value = this.#destination.province;
    this.#get('zip').value = this.#destination.zip;
    this.#get('dialog').showModal();
    this.#get('change').setAttribute('aria-expanded', 'true');
    this.#position();
  };
  #position = () => {
    const dialog = this.#get('dialog');
    if (!dialog.open || !matchMedia('(min-width: 750px)').matches) return;
    const anchor = this.#get('change').getBoundingClientRect();
    const rect = dialog.getBoundingClientRect();
    const left = Math.max(16, Math.min(window.innerWidth - rect.width - 16, anchor.right - rect.width));
    const top = anchor.top - rect.height - 12 >= 16 ? anchor.top - rect.height - 12 : Math.max(16, Math.min(anchor.bottom + 12, window.innerHeight - rect.height - 16));
    dialog.style.setProperty('--shipping-dialog-left', `${left}px`);
    dialog.style.setProperty('--shipping-dialog-top', `${top}px`);
  };
  #apply = event => {
    event.preventDefault();
    if (!this.#get('form').reportValidity()) return;
    this.#destination = { country_code: this.#get('country').value, province: this.#get('province').disabled ? '' : this.#get('province').value, zip: this.#get('zip').value.trim() };
    try { sessionStorage.setItem('fivepoint.shipping-destination', JSON.stringify(this.#destination)); } catch { /* Optional preference only. */ }
    this.#destinationLabel(); this.#get('dialog').close(); this.#key = ''; this.#sync();
  };
  #destinationLabel() {
    const country = [...this.#get('country').options].find(o => o.value === this.#destination.country_code);
    this.#get('destination').textContent = [country?.textContent, this.#destination.province, this.#destination.zip].filter(Boolean).join(' · ');
  }
  #sync = () => {
    const variant = this.#root.querySelector('[data-fp-variant-id]')?.value;
    const quantity = Number(this.#root.querySelector('[data-fp-quantity]')?.value);
    const input = { variant_id: variant, quantity, ...this.#destination, currency: this.dataset.currency };
    const key = JSON.stringify(input);
    if (key === this.#key) return;
    this.#key = key; this.#request?.abort(); clearTimeout(this.#timer); const version = ++this.#version;
    this.#get('result').hidden = true; this.#get('retry').hidden = true; this.removeAttribute('aria-busy');
    if (!/^[1-9]\d*$/.test(variant || '') || !Number.isSafeInteger(quantity) || quantity < 1) { this.#get('status').textContent = this.#text('choose_colour'); return; }
    if (!this.#destination.country_code || (this.#destination.country_code === 'US' && (!this.#destination.province || !this.#destination.zip))) { this.#get('status').textContent = this.#text('choose_destination'); return; }
    this.setAttribute('aria-busy', 'true'); this.#get('status').textContent = this.#text('loading');
    this.#timer = setTimeout(() => this.#quote(input, version), 220);
  };
  async #quote(input, version) {
    const controller = new AbortController(); this.#request = controller;
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(this.dataset.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input), signal: controller.signal });
      const quote = await response.json();
      if (version !== this.#version || !this.isConnected) return;
      if (!response.ok) {
        const error = new Error(this.#text(quote.code === 'no_shipping_rates' ? 'no_rates' : quote.code === 'shipping_address_or_item_invalid' ? 'invalid_address' : 'failed'));
        throw error;
      }
      if (quote.currency !== input.currency || !Array.isArray(quote.rates) || !quote.rates.length || quote.rates.some(r => !r.title || !/^\d+(\.\d+)?$/.test(r.amount) || !/^\d+(\.\d+)?$/.test(r.total))) throw new Error(this.#text('failed'));
      const money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: quote.currency });
      const rates = this.#get('rates'); rates.replaceChildren();
      for (const rate of quote.rates) {
        const card = document.createElement('div'); card.className = 'fp-shipping-rate';
        const label = document.createElement('span'); label.textContent = ['standard','express'].includes(rate.kind) ? this.#text(rate.kind) : rate.title;
        const price = document.createElement('strong'); price.textContent = Number(rate.amount) === 0 ? this.#text('free') : money.format(Number(rate.amount));
        card.append(label, price); rates.append(card);
      }
      const selectionKey = this.#root.querySelector('[data-fp-foot]')?.dataset.tier === 'swatch' ? 'selection_cards' : 'selection';
      this.#get('selection').textContent = this.#text(selectionKey).replace('{quantity}', String(input.quantity));
      const standard = quote.rates.find(r => r.kind === 'standard');
      this.#get('total').hidden = !standard;
      if (standard) { this.#get('total-label').textContent = this.#text('total'); this.#get('total-value').textContent = money.format(Number(standard.total)); }
      this.#get('status').textContent = ''; this.#get('result').hidden = false;
    } catch (error) {
      if (version !== this.#version || !this.isConnected) return;
      this.#get('status').textContent = error.name === 'AbortError' ? this.#text('failed') : error.message === this.#text('no_rates') || error.message === this.#text('invalid_address') ? error.message : this.#text('failed');
      this.#get('retry').hidden = false;
    } finally {
      clearTimeout(timer);
      if (version === this.#version) this.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('fivepoint-shipping-estimate')) customElements.define('fivepoint-shipping-estimate', FivepointShippingEstimate);
